import { Fragment, lazy, useState } from 'react';
import { BlockBody, BlockContainer, BlockHeader } from '@medifind/containers';
import { IconArrowSmall, IconArrowUpDown } from '@medifind/icons';
import { MFLink, Skeleton } from '@medifind/shared-basic-components';
import { eventDispatch, getNullList, isPrerender } from '@medifind/utils';
import styles from './DoctorProfileClinicalResearch.module.scss';

const ClinicalTrialCardV3 = lazy(() => import('@medifind/lazy-loadable/lib/search-results/LazyClinicalTrialCardV3'));
const PublicationCardV3 = lazy(() => import('@medifind/lazy-loadable/lib/search-results/LazyPublicationCardV3'));

const DoctorProfileClinicalResearch = ({ doctor, profile, personSlug, articles, trials, classes }) => {
  const [showTrials, setShowTrials] = useState(isPrerender);
  const toggleTrials = () => setShowTrials(!showTrials);

  const { personId, name, displayName } = doctor || {};

  const { totalResults: trialsTotalResults, results: trialsResults, resultsRecruiting: trialsResultsRecruiting } = trials || {};
  const { totalResults: articlesTotalResults, results: articlesResults } = articles || {};

  const showLogin = (e, name, url) => {
    if (!profile) {
      eventDispatch('activateAuth', {
        errorMessage: `You can view more publications of ${name} with a MediFind account.`,
        authType: 'login',
        icon: 'next',
        redirectAction: 'page',
        redirectData: url,
      });
      e.preventDefault();
    }
  };

  return (
    <BlockContainer id="research" className={classes.block}>
      <BlockHeader priority={2} className={classes.blockHeader}>
        Clinical Research
      </BlockHeader>
      <BlockBody className={classes.blockBody}>
        <p>
          Clinical research consists of overseeing clinical studies of patients undergoing new treatments and therapies, and publishing
          articles in peer reviewed medical journals. {doctor?.isUSPrescriber ? 'Providers' : 'Experts'} who actively participate in
          clinical research are generally at the forefront of the fields and aware of the most up-to-date advances in treatments for their
          patients.
        </p>

        <hr />

        {(trialsTotalResults === 0 && articlesTotalResults === 0) ||
        (trialsTotalResults == null && articlesTotalResults == null && trials && articles) ? (
          <>
            {/* TODO replace this copy? */}
            <p>MediFind cannot find any recent clinical research or clinical trials for {displayName}</p>
          </>
        ) : null}

        {/* trials */}
        {/* {trials && trialsTotalResults > 0 ? ( */}
        <div className={styles['trials']}>
          {trialsTotalResults || !trials ? (
            <>
              {/* <MFLink
                onClick={e => {
                  e.preventDefault();
                  toggleTrials();
                }}
                className={styles['trials__expander']}
              > */}
              <h2 className={styles['header']}>
                <span className={styles['text']}>{trialsTotalResults ? trialsTotalResults : <Skeleton width={20} />} Clinical Trials</span>
              </h2>

              {/* {trialsTotalResults > 1 &&
               (showTrials ? <span className={styles['plus']}>-</span> : <span className={styles['plus']}>+</span>)} */}
              {/* </MFLink> */}
            </>
          ) : null}

          {showTrials ? (
            <>
              {(trials ? (trialsResultsRecruiting || []).concat(trialsResults || []) : getNullList(1)).map((trial, i) => (
                <Fragment key={`${i}-${trial?.clinicalTrialId}`}>
                  <ClinicalTrialCardV3
                    fieldsToHide={['patientPopulation']}
                    trial={trial}
                    hideIcon={true}
                    showSummary={false}
                    smallAt={['xs', 'sm', 'lg']}
                  />
                </Fragment>
              ))}
            </>
          ) : (
            <>
              {(trials
                ? trialsResultsRecruiting?.length === 0
                  ? trialsResults?.slice(0, 1)
                  : trialsResultsRecruiting || []
                : getNullList(1)
              ).map((trial, i) => {
                return (
                  <Fragment key={`${i}-${trial?.clinicalTrialId}`}>
                    <ClinicalTrialCardV3
                      fieldsToHide={['patientPopulation']}
                      trial={trial}
                      hideIcon={true}
                      showSummary={false}
                      smallAt={['xs', 'sm', 'lg']}
                    />
                  </Fragment>
                );
              })}
            </>
          )}
          {/* Subtract by one if no recruiting trials as this means that one of the results is already shown */}
          {trialsResults?.length - (trialsResultsRecruiting?.length === 0 ? 1 : 0) >= 1 || !trials ? (
            <>
              {trialsResults?.length - (trialsResultsRecruiting?.length === 0 ? 1 : 0) >= 1 ? (
                <>
                  <div style={{ textAlign: 'center' }}>
                    <MFLink className={styles['link']} onClick={toggleTrials}>
                      View {trialsResults?.length - (trialsResultsRecruiting?.length === 0 ? 1 : 0)} {showTrials ? 'Less' : 'More'} Clinical
                      Trial
                      {trialsResults?.length > 2 ? 's' : ''} <IconArrowUpDown direction={showTrials ? 'up' : 'down'} />
                    </MFLink>
                  </div>
                </>
              ) : (
                <Skeleton />
              )}
            </>
          ) : null}
        </div>
        {/* ) : null} */}

        {/* articles */}
        {/* {articlesTotalResults > 0 || !articles ? ( */}
        <div className={styles['articles']}>
          {(articles ? articlesResults?.slice(0, 1) || [] : getNullList(1)).map((article, i) => (
            <Fragment key={`${i}-${article?.publicationId}`}>
              {i === 0 && (
                <>
                  <MFLink className={styles['articles__expander']}>
                    <h2 className={styles['header']}>
                      <span className={styles['text']}>{articlesTotalResults > 0 ? `${articlesTotalResults} Total Publications` : ''}</span>
                    </h2>
                  </MFLink>
                </>
              )}
              <PublicationCardV3
                id={article?.publicationPersonifyId}
                publication={article}
                personSlug={personSlug}
                personId={personId}
                hideIcon
                smallAt={['xs', 'sm', 'lg']}
              />
            </Fragment>
          ))}

          {articlesTotalResults > 1 || !articles ? (
            <>
              {personId && articlesTotalResults > 1 ? (
                <>
                  <div style={{ textAlign: 'center' }}>
                    {profile ? (
                      <MFLink
                        to={`/doctors/${personSlug}/${personId}/publications`}
                        className={styles['link']}
                        clickOverride={(e) => {
                          const n = displayName || name;
                          showLogin(e, n, `/doctors/${personSlug}/${personId}/publications`);
                        }}
                      >
                        View All {articlesTotalResults} Publications <IconArrowSmall />
                      </MFLink>
                    ) : (
                      <MFLink
                        style={{ cursor: 'pointer' }}
                        className={styles['link']}
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          const n = displayName || name;
                          showLogin(e, n, `/doctors/${personSlug}/${personId}/publications`);
                        }}
                      >
                        View All {articlesTotalResults} Publications <IconArrowSmall />
                      </MFLink>
                    )}
                  </div>
                </>
              ) : (
                <Skeleton />
              )}
            </>
          ) : null}
        </div>
        {/* ) : null} */}
      </BlockBody>
    </BlockContainer>
  );
};

export { DoctorProfileClinicalResearch as default, DoctorProfileClinicalResearch };
